// Opmaak met Intl in de taal van de gebruiker. Feiten worden nooit vertaald, alleen geformatteerd.
let organizationTimeZone: string | undefined;

function displayTimeZone(): string | undefined {
  return typeof window !== "undefined" && window.location.pathname.startsWith("/platform") ? undefined : organizationTimeZone;
}

/** De actieve organisatie bepaalt tijden; het platform volgt de tijdzone van de gebruiker. */
export function setFormatTimeZone(timeZone: string | undefined): void {
  organizationTimeZone = timeZone;
}
export function money(minor: number | null | undefined, currency = "EUR", tag = "nl-NL"): string {
  if (minor == null) return "–";
  return new Intl.NumberFormat(tag, { style: "currency", currency, maximumFractionDigits: 0 }).format(minor / 100);
}

export function number(value: number | null | undefined, tag = "nl-NL", digits = 0): string {
  if (value == null) return "–";
  return new Intl.NumberFormat(tag, { maximumFractionDigits: digits }).format(value);
}

export function area(m2: number | null | undefined, tag = "nl-NL"): string {
  if (m2 == null) return "–";
  return `${new Intl.NumberFormat(tag, { maximumFractionDigits: 0 }).format(m2)} m²`;
}

export function dateTime(iso: string | Date | null | undefined, tag = "nl-NL", timeZone = displayTimeZone()): string {
  if (!iso) return "–";
  return new Intl.DateTimeFormat(tag, { dateStyle: "medium", timeStyle: "short", timeZone }).format(new Date(iso));
}

export function date(iso: string | Date | null | undefined, tag = "nl-NL", timeZone = displayTimeZone()): string {
  if (!iso) return "–";
  return new Intl.DateTimeFormat(tag, { dateStyle: "medium", timeZone }).format(new Date(iso));
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** "3 minuten geleden", "over 2 uur". */
export function relative(iso: string | Date | null | undefined, tag = "nl-NL", now = Date.now()): string {
  if (!iso) return "–";
  const seconds = Math.round((new Date(iso).getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
  for (const [unit, size] of UNITS) if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  // Minder dan een minuut: "nu" / "now" (format(0, "minute") gaf "binnen een minuut").
  return rtf.format(0, "second");
}

export function bytes(n: number, tag = "nl-NL"): string {
  const units = ["B", "kB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${new Intl.NumberFormat(tag, { maximumFractionDigits: i ? 1 : 0 }).format(v)} ${units[i]}`;
}

const SHORT: [Intl.NumberFormatOptions["unit"], number][] = [
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
];

/** Kort en op één regel voor lijsten ("22u", "3d"); ouder dan vier weken wordt een datum. Volledige tijd hoort in de title. */
export function ago(iso: string | Date | null | undefined, tag = "nl-NL", now = Date.now()): string {
  if (!iso) return "–";
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (seconds >= 28 * 24 * 3600) return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short", timeZone: displayTimeZone() }).format(new Date(iso));
  // Minuten kort ("20 min", nooit "20 m" wat op meters lijkt); uren, dagen en weken smal ("22u", "3d").
  for (const [unit, size] of SHORT) if (seconds >= size) return new Intl.NumberFormat(tag, { style: "unit", unit, unitDisplay: unit === "minute" ? "short" : "narrow" }).format(Math.floor(seconds / size));
  return new Intl.RelativeTimeFormat(tag, { numeric: "auto" }).format(0, "second");
}
